import { isElectron } from '../utils/electron';
import { getFullPathFromOrigin } from '../utils/path';

// The demo catalog served alongside a hosted DirPlayer build.
//
// A landing page (dirplayer.com) wants to offer a handful of ready-to-play
// movies, which REACT_APP_MOVIE_URL can only do by baking ONE movie into the
// bundle — so every demo costs a whole separate build, and adding or renaming
// one means rebuilding and redeploying it.
//
// The catalog moves that list out of the bundle and into a JSON file served
// next to it, read at runtime. One build serves every demo, and the list is
// edited by editing `movies.json` on the server.
//
// Everything here is defensive: a missing, unreachable, non-JSON or malformed
// file yields no catalog and the load screen renders exactly as it does today.
// Self-hosted builds that never add the file are unaffected.
//
// See public/movies.example.json for the file format.

const CATALOG_FILE = 'movies.json';
const DEFAULT_TITLE = 'Demos';

// Structurally identical to LoadMovie's ExternalParam, so a catalog entry's
// params drop straight into the loader without a conversion step.
export type CatalogParam = { key: string; value: string };

export type CatalogMovie = {
  // The entry's slug: its React key, and what `?movie=` names to deep-link
  // straight into it. Falls back to the entry's position, so two entries may
  // share a url (the same movie against two different servers) without one
  // of them disappearing from the list.
  id: string;
  // Additional slugs that resolve to this entry. The reason these exist is
  // link permanence: the site published a page per demo before it published a
  // catalog, and those URLs have to keep working after the id is chosen
  // independently of them.
  aliases: string[];
  // The display name. This is the whole point of the catalog on a landing
  // page: "Habbo Hotel V7" instead of `dcr_woodpecker/habbo.dcr`.
  name: string;
  description?: string;
  url: string;
  params: CatalogParam[];
  fakeMoviePath?: string;
  launchCommand?: string;
  useCorsProxy?: boolean;
};

export type MovieCatalog = {
  title: string;
  movies: CatalogMovie[];
};

function optionalString(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value.trim() : undefined;
}

// The file writes params as an object (`{"sw1": "..."}`) because that is what
// is pleasant to hand-edit. The array form is accepted too so an entry can be
// pasted straight out of a `recentMovies` localStorage dump — the fastest way
// to turn a movie you got working locally into a published demo.
function parseParams(raw: unknown): CatalogParam[] {
  if (!raw || typeof raw !== 'object') return [];
  const entries: [unknown, unknown][] = Array.isArray(raw)
    ? raw.map(p => [(p as CatalogParam)?.key, (p as CatalogParam)?.value])
    : Object.entries(raw as Record<string, unknown>);
  return entries.flatMap(([key, value]) => {
    const k = optionalString(key);
    return k !== undefined && typeof value === 'string' ? [{ key: k, value }] : [];
  });
}

// Name an unnamed entry after its file, matching how the recent list labels
// rows. Never blank: a nameless row is unclickable in practice.
function fileNameOf(url: string): string {
  const path = url.split(/[?#]/)[0].replace(/[/\\]+$/, '');
  return path.split(/[/\\]/).pop() || url;
}

function parseMovie(raw: unknown, index: number): CatalogMovie | null {
  if (!raw || typeof raw !== 'object') return null;
  const entry = raw as Record<string, unknown>;
  const rawUrl = optionalString(entry.url);
  if (!rawUrl) return null;
  // Catalogue entries are written relative to the app ("dcr_v31/habbo.dcr") so
  // the same file works on any host, but the player needs an absolute base
  // path — a relative one panics the VM in `set_base_path`. Resolve here, once,
  // so `url` means the same absolute thing as it does in a recent entry: the
  // demo's name can then be matched back onto its row in Recent Movies.
  // Absolute urls pass through untouched.
  const url = getFullPathFromOrigin(rawUrl);
  return {
    id: optionalString(entry.id) ?? `${index}:${url}`,
    aliases: Array.isArray(entry.aliases)
      ? entry.aliases.flatMap(a => optionalString(a) ?? [])
      : [],
    name: optionalString(entry.name) ?? fileNameOf(url),
    description: optionalString(entry.description),
    url,
    params: parseParams(entry.params),
    fakeMoviePath: optionalString(entry.fakeMoviePath),
    launchCommand: optionalString(entry.launchCommand),
    useCorsProxy: entry.useCorsProxy === true,
  };
}

export function parseMovieCatalog(raw: unknown): MovieCatalog | null {
  if (!raw || typeof raw !== 'object') return null;
  const root = raw as Record<string, unknown>;
  // A bare array is accepted as shorthand for `{ "movies": [...] }`.
  const list = Array.isArray(raw) ? raw : root.movies;
  if (!Array.isArray(list)) return null;
  const movies = list.flatMap((entry, i) => parseMovie(entry, i) ?? []);
  if (movies.length === 0) return null;
  return {
    title: (!Array.isArray(raw) && optionalString(root.title)) || DEFAULT_TITLE,
    movies,
  };
}

// The query parameter that deep-links into a catalog entry: `/?movie=junkbot`.
// A query rather than a path, because the app has to stay at the site root —
// every movie url in the catalog resolves against the page's own directory, so
// serving the same build under `/junkbot/` would send the asset requests to
// `/junkbot/dcr_lego/…`. The old per-demo pages redirect here instead; see
// scripts/generate-demo-links.mjs.
export const MOVIE_QUERY_PARAM = 'movie';

// Every slug that addresses this entry. Compared case-insensitively: these
// come out of hand-written URLs.
function slugsOf(movie: CatalogMovie): string[] {
  return [movie.id, ...movie.aliases].map(s => s.toLowerCase());
}

// The catalog entry named by the current URL's `?movie=`, if any.
export function findRequestedMovie(movies: CatalogMovie[]): CatalogMovie | null {
  let requested: string | null = null;
  try {
    requested = new URLSearchParams(window.location.search).get(MOVIE_QUERY_PARAM);
  } catch {
    return null;
  }
  const slug = requested?.trim().toLowerCase();
  if (!slug) return null;
  return movies.find(movie => slugsOf(movie).includes(slug)) ?? null;
}

export async function fetchMovieCatalog(): Promise<MovieCatalog | null> {
  // The packaged desktop app is served off `file://`, where Chromium refuses
  // the fetch outright. Nothing to gain by trying, and skipping keeps a failed
  // request out of the console on every launch.
  if (isElectron()) return null;
  try {
    // Resolved against the app's own directory, like every other bundled
    // asset, so the catalog works from a subpath deploy (dirplayer.com/beta/).
    // `no-cache` revalidates: editing the file on the server has to be enough
    // to change the list, which a cached copy would defeat.
    const response = await fetch(getFullPathFromOrigin(CATALOG_FILE), { cache: 'no-cache' });
    if (!response.ok) return null;
    // A static host that answers unknown paths with index.html hands us HTML
    // here; `json()` rejects and the catch below turns that into "no catalog".
    return parseMovieCatalog(await response.json());
  } catch {
    // No catalog is the normal case for a self-hosted build. Not an error.
    return null;
  }
}
